/* eslint-disable no-underscore-dangle */
/* eslint-disable default-param-last */
import { Reducer } from 'redux';
import produce from 'immer';
import { MarketTypes, MarketState, ActivitiesTypes, CompanyTypes } from './types';

const EMPTY_COMPANY_PROFILE: Omit<CompanyTypes, 'biddings' | 'total_result' | 'certidoes'> = {
  id: 0,
  razao_social: '',
  logradouro: '',
  numero: '',
  bairro: '',
  municipio: '',
  uf: '',
  telefone: '',
  email: '',
  situacao: '',
  tipo_empresa: '',
  natureza_juridica: '',
  abertura: '',
  quadro_socios_administradores: [],
  capital_social: 0,
  cnaes: [],
  cnpj: '',
  sancoes: [],
};

const INITIAL_STATE: MarketState = {
  market: {
    date_range: [],
    date_range_accumulated: [],
    total_entries: 0,
    total_investimento: 0,
    estados: {
      regions: [],
    },
    modalities: [],
    activities: [],
  },
  get_filters: [],
  orgao: {
    total: 0,
    total_pages: 0,
    results: [],
  },
  concorrentes: {
    total: 0,
    total_pages: 0,
    results: [],
  },
  buyer: {
    cep: '',
    cidade: '',
    edital_site: '',
    endereco: '',
    estado: '',
    nome: '',
    uasg: '',
    licitacoes: [],
  },
  company: {
    ...EMPTY_COMPANY_PROFILE,
    biddings: [],
    total_result: 0,
    certidoes: [],
  },
  resultado: {
    resultados: [],
    total: 0,
  },
  products_services: {
    products: [],
    total: 0,
  },
  bidding: {
    licitacao: [],
    total: 0,
  },
  bidding_result: {
    licitacao: {
      edital: '',
      edital_status_id: '' || null,
      id: 0,
      objeto: '',
      observacao: '',
      processo: '',
      situacao: '',
      edicts: [],
      datas: {
        datahora_abertura: '' || null,
        datahora_documento: '' || null,
        datahora_prazo: '' || null,
        datahora_retirada: '' || null,
        datahora_visita: '' || null,
      },
    },
    orgao: {
      site: '',
      endereco: '',
      cidade_uf: '',
      uasg: '',
      nome: '',
    },
    resultados: [],
    acompanhamentos: [],
  },
  request: {
    search: {
      loading: false,
      loaded: false,
      error: false,
    },
    get_filters: {
      loading: false,
      loaded: false,
      error: false,
    },
    search_buyers: {
      loading: false,
      loaded: false,
      error: false,
    },
    search_providers: {
      loading: false,
      loaded: false,
      error: false,
    },
    filter_options: {
      loading: false,
      loaded: false,
      error: false,
    },
    get_public_body: {
      loading: false,
      loaded: false,
      error: false,
    },
    get_bidding: {
      loading: false,
      loaded: false,
      error: false,
    },
    get_company: {
      loading: false,
      loaded: false,
      error: false,
    },
    get_provider_biddings: {
      loading: false,
      loaded: false,
      error: false,
    },
    get_certidoes: {
      loading: false,
      loaded: false,
      error: false,
    },
    get_results: {
      loading: false,
      loaded: false,
      error: false,
    },
    get_products: {
      loading: false,
      loaded: false,
      error: false,
    },
    get_followups: {
      loading: false,
      loaded: false,
      error: false,
    },
    bidding_result: {
      loading: false,
      loaded: false,
      error: false,
    },
  },
};

const reducer: Reducer<MarketState> = (state = INITIAL_STATE, { type, result }) => {
  return produce(state, (draft) => {
    switch (type) {
      case MarketTypes.SEARCH_REQUEST:
        draft.request.search = { loading: true, loaded: false, error: false };
        break;
      case MarketTypes.SEARCH_SUCCESS:
        draft.market = result?.data;
        draft.request.search = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      case MarketTypes.SEARCH_FAILURE:
        draft.market = INITIAL_STATE.market;
        draft.request.search = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case MarketTypes.GET_FILTERS_REQUEST:
        draft.request.get_filters = { loading: true, loaded: false, error: false };
        break;
      case MarketTypes.GET_FILTERS_SUCCESS:
        draft.get_filters = result?.data;
        draft.request.get_filters = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      case MarketTypes.GET_FILTERS_FAILURE:
        draft.request.get_filters = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case MarketTypes.SEARCH_BUYERS_REQUEST:
        draft.orgao = INITIAL_STATE.orgao;
        draft.request.search_buyers = { loading: true, loaded: false, error: false };
        break;
      case MarketTypes.SEARCH_BUYERS_SUCCESS:
        draft.orgao = result?.data?.buyers;
        draft.request.search_buyers = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      case MarketTypes.SEARCH_BUYERS_FAILURE:
        draft.orgao = INITIAL_STATE.orgao;
        draft.request.search_buyers = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case MarketTypes.SEARCH_PROVIDERS_REQUEST:
        draft.request.search_providers = { loading: true, loaded: false, error: false };
        break;
      case MarketTypes.SEARCH_PROVIDERS_SUCCESS:
        draft.concorrentes = result?.data?.providers;
        draft.request.search_providers = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      case MarketTypes.SEARCH_PROVIDERS_FAILURE:
        draft.request.search_providers = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case MarketTypes.GET_FILTER_OPTIONS_REQUEST:
        draft.request.filter_options = { loading: true, loaded: false, error: false };
        break;
      case MarketTypes.GET_FILTER_OPTIONS_SUCCESS:
        draft.market.activities =
          (Object.entries(result?.data?.activities)?.map((activity) => {
            return activity[1];
          }) as Array<ActivitiesTypes>) || [];

        draft.request.filter_options = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      case MarketTypes.GET_FILTER_OPTIONS_FAILURE:
        draft.market.activities = INITIAL_STATE.market.activities;
        draft.request.filter_options = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case MarketTypes.GET_PUBLIC_BODY_REQUEST:
        draft.request.get_public_body = { loading: true, loaded: false, error: false };
        break;
      case MarketTypes.GET_PUBLIC_BODY_SUCCESS:
        draft.buyer.nome = result?.data?.dados?.nome;
        draft.buyer.uasg = result?.data?.dados?.uasg;
        draft.buyer.edital_site = result?.data?.dados?.edital_site;
        draft.buyer.endereco = result?.data?.dados?.endereco;
        draft.buyer.cidade = result?.data?.cidade;
        draft.buyer.estado = result?.data?.uf;

        draft.request.get_public_body = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      case MarketTypes.GET_PUBLIC_BODY_FAILURE:
        draft.request.get_public_body = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case MarketTypes.GET_BIDDINGS_REQUEST:
        draft.request.get_bidding = { loading: true, loaded: false, error: false };
        break;
      case MarketTypes.GET_BIDDINGS_SUCCESS:
        draft.buyer.licitacoes = result?.data?.licitacoes;
        draft.request.get_bidding = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      case MarketTypes.GET_BIDDINGS_FAILURE:
        draft.request.get_bidding = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case MarketTypes.GET_COMPANY_DATA_REQUEST:
        Object.assign(draft.company, EMPTY_COMPANY_PROFILE);
        draft.request.get_company = { loading: true, loaded: false, error: false };
        break;
      case MarketTypes.GET_COMPANY_DATA_SUCCESS: {
        const empresa = result?.data?.data?.empresa;

        Object.assign(draft.company, {
          ...EMPTY_COMPANY_PROFILE,
          id: empresa?.id || 0,
          razao_social: empresa?.razao_social || '',
          cnpj: empresa?.cnpj || '',
          logradouro: empresa?.logradouro || '',
          numero: empresa?.numero || '',
          bairro: empresa?.bairro || '',
          municipio: empresa?.municipio || '',
          uf: empresa?.uf || '',
          telefone: empresa?.telefone || '',
          email: empresa?.email || '',
          situacao: empresa?.situacao || '',
          tipo_empresa: empresa?.tipo_empresa || '',
          natureza_juridica: empresa?.natureza_juridica || '',
          abertura: empresa?.abertura || '',
          capital_social: empresa?.capital_social || 0,
          quadro_socios_administradores: empresa?.quadro_socios_administradores || [],
          cnaes: empresa?.cnaes || [],
          sancoes: result?.data?.sancoes || [],
        });
        draft.request.get_company = {
          loading: false,
          loaded: !!empresa,
          error: !empresa,
        };
        break;
      }
      case MarketTypes.GET_COMPANY_DATA_FAILURE:
        Object.assign(draft.company, EMPTY_COMPANY_PROFILE);
        draft.request.get_company = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case MarketTypes.GET_PROVIDER_BIDDINGS_REQUEST:
        draft.request.get_provider_biddings = { loading: true, loaded: false, error: false };
        break;
      case MarketTypes.GET_PROVIDER_BIDDINGS_SUCCESS:
        draft.company.biddings = result?.data?.provider?.results;
        draft.company.total_result = result?.data?.provider?.total;
        draft.request.get_provider_biddings = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      case MarketTypes.GET_PROVIDER_BIDDINGS_FAILURE:
        draft.request.get_provider_biddings = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case MarketTypes.GET_CERTIDOES_REQUEST:
        draft.request.get_certidoes = { loading: true, loaded: false, error: false };
        break;
      case MarketTypes.GET_CERTIDOES_SUCCESS:
        draft.request.get_certidoes = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      case MarketTypes.GET_CERTIDOES_FAILURE:
        draft.request.get_certidoes = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case MarketTypes.GET_RESULTS_REQUEST:
        draft.request.get_results = { loading: true, loaded: false, error: false };
        break;
      case MarketTypes.GET_RESULTS_SUCCESS:
        draft.resultado.resultados = result?.data?.lista_resultados?.resultados;
        draft.resultado.total = result?.data?.lista_resultados?.total;
        draft.request.get_results = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      case MarketTypes.GET_RESULTS_FAILURE:
        draft.request.get_results = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case MarketTypes.PRODUCTS_SERVICES_REQUEST:
        draft.products_services.products = INITIAL_STATE.products_services.products;

        draft.request.get_products = { loading: true, loaded: false, error: false };
        break;
      case MarketTypes.PRODUCTS_SERVICES_SUCCESS:
        draft.products_services.products = result?.data?.produtos_e_servicos?.produtos;
        draft.products_services.total = result?.data?.produtos_e_servicos?.total;
        draft.request.get_products = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      case MarketTypes.PRODUCTS_SERVICES_FAILURE:
        draft.products_services.products = INITIAL_STATE.products_services.products;
        draft.request.get_products = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;

      case MarketTypes.GET_FOLLOWUPS_REQUEST:
        draft.bidding.licitacao = INITIAL_STATE.bidding?.licitacao;
        draft.bidding.total = INITIAL_STATE.bidding?.total;
        draft.request.get_followups = { loading: true, loaded: false, error: false };
        break;
      case MarketTypes.GET_FOLLOWUPS_SUCCESS:
        draft.bidding.licitacao = result?.data?.acompanhamentos?.lista_acompanhamentos;
        draft.bidding.total = result?.data?.acompanhamentos?.total;
        draft.request.get_followups = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      case MarketTypes.GET_FOLLOWUPS_FAILURE:
        draft.bidding.licitacao = INITIAL_STATE.bidding?.licitacao;
        draft.bidding.total = INITIAL_STATE.bidding?.total;
        draft.request.get_followups = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case MarketTypes.GET_RESULT_BIDDING_REQUEST:
        draft.request.bidding_result = { loading: true, loaded: false, error: false };
        break;
      case MarketTypes.GET_RESULT_BIDDING_SUCCESS:
        draft.bidding_result.licitacao = result?.data?.licitacao;
        draft.bidding_result.orgao = result?.data?.orgao;
        draft.bidding_result.resultados = result?.data?.resultados;
        draft.bidding_result.acompanhamentos = result?.data?.acompanhamentos;
        draft.request.bidding_result = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      case MarketTypes.GET_RESULT_BIDDING_FAILURE:
        draft.request.bidding_result = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      default:
    }
  });
};

export default reducer;
